import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Search, MoreHorizontal, Copy, Trash2, Archive, Eye, PencilLine, FileText, FolderUp,
} from "lucide-react";
import DuplicatePlanDialog from "@/components/admin/DuplicatePlanDialog";

const STATUS_STYLES = {
  draft: "bg-yellow-100 text-yellow-800 border-yellow-200",
  published: "bg-green-100 text-green-800 border-green-200",
  archived: "bg-slate-200 text-slate-700 border-slate-300",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`plan-status-${status || "draft"}`}
    className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[status || "draft"]}`}
  >
    {status || "draft"}
  </span>
);

const AdminPlansList = ({ channel = null } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialStatus = searchParams.get("status") || "all";
  const [status, setStatus] = useState(initialStatus);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  // Iter56 — Duplicate Plan Dialog state (opens on row action + editor toolbar)
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Iter50 (Item #9) — Extended filter + sort state.
  const [style, setStyle] = useState("all");
  const [category, setCategory] = useState("all");
  const [collection, setCollection] = useState("all");
  const [beds, setBeds] = useState("all");
  const [baths, setBaths] = useState("all");
  const [featured, setFeatured] = useState("all");
  const [createdBy, setCreatedBy] = useState("all");
  const [modifiedBy, setModifiedBy] = useState("all");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterOpts, setFilterOpts] = useState({ categories: [], collections: [], bedrooms: [], bathrooms: [], created_by: [], modified_by: [] });
  const [stylesMaster, setStylesMaster] = useState([]);
  useEffect(() => {
    api.get("/admin/plans/filter-options").then(({ data }) => setFilterOpts(data)).catch(() => {});
    api.get("/styles/detailed").then(({ data }) => setStylesMaster(data.items || [])).catch(() => {});
  }, []);

  // sync status from URL
  useEffect(() => {
    setStatus(searchParams.get("status") || "all");
    setSkip(0);
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (debouncedQ) params.set("search", debouncedQ);
      // Iter50 (Item #9) — send extended filter params
      if (style !== "all") params.set("style", style);
      if (category !== "all") params.set("category", category);
      if (collection !== "all") params.set("collection", collection);
      if (beds !== "all") params.set("bedrooms", String(beds));
      if (baths !== "all") params.set("bathrooms", String(baths));
      if (featured !== "all") params.set("featured", featured);
      if (createdBy !== "all") params.set("created_by", createdBy);
      if (modifiedBy !== "all") params.set("modified_by", modifiedBy);
      if (priceMin !== "") params.set("price_min", String(priceMin));
      if (priceMax !== "") params.set("price_max", String(priceMax));
      if (sortBy) params.set("sort_by", sortBy);
      if (sortDir) params.set("sort_dir", sortDir);
      // Iter60A — channel-scoped list (used by /admin/plans/floor).
      if (channel) params.set("channel", channel);
      params.set("skip", String(skip));
      params.set("limit", String(limit));
      const { data } = await api.get(`/admin/plans?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load plans", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setLoading(false);
    }
  }, [status, debouncedQ, style, category, collection, beds, baths, featured, createdBy, modifiedBy, priceMin, priceMax, sortBy, sortDir, skip, limit, channel]);

  useEffect(() => { load(); }, [load]);

  const allSelected = items.length > 0 && items.every((p) => selected.includes(p.id));
  const toggleAll = () => setSelected(allSelected ? [] : items.map((p) => p.id));
  const toggleOne = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const runBulk = async (action) => {
    if (selected.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.length} plan(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/plans/bulk", { action, ids: selected });
      toast.success(`Bulk ${action} complete`, {
        description: data.deleted != null ? `Deleted ${data.deleted}` : `Modified ${data.modified}`,
      });
      setSelected([]);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error("Bulk action failed", { description: e.response?.data?.detail || "Try again." });
    }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/plans/${id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) {
      toast.error("Failed to update status", { description: e.response?.data?.detail || "Try again." });
    }
  };

  const duplicate = (planOrId) => {
    // Iter56 — open the DuplicatePlanDialog. Accepts either a full row or
    // just an id (for backwards-compat with older row menu wiring).
    if (typeof planOrId === "string") {
      const p = items.find((x) => x.id === planOrId) || { id: planOrId };
      setDuplicateTarget(p);
    } else {
      setDuplicateTarget(planOrId);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this plan permanently?")) return;
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || "Try again." });
    }
  };

  const setStatusFilter = (v) => {
    setStatus(v);
    const params = new URLSearchParams(searchParams);
    if (v === "all") params.delete("status");
    else params.set("status", v);
    setSearchParams(params);
    setSkip(0);
  };

  const pages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  const emptyMsg = useMemo(() => {
    if (loading) return null;
    if (debouncedQ) return `No plans match "${debouncedQ}".`;
    if (status !== "all") return `No ${status} plans yet.`;
    return "No plans yet — create your first one.";
  }, [loading, debouncedQ, status]);

  return (
    <AdminLayout
      title={channel === "floor_plan" ? "Floor Plans" : "House plans"}
      actions={
        <div className="flex gap-2">
          <Button
            data-testid="bulk-import-btn"
            onClick={() => navigate("/admin/plans/bulk-import")}
            variant="outline"
            className="rounded-none"
          >
            <FolderUp size={14} className="mr-2" /> Bulk import
          </Button>
          <Button
            data-testid={channel === "floor_plan" ? "new-floor-plan-btn" : "new-plan-btn"}
            onClick={() => navigate(channel === "floor_plan" ? "/admin/plans/new?channel=floor_plan" : "/admin/plans/new")}
            className="rounded-none bg-accent hover:bg-accent/90"
          >
            <Plus size={14} className="mr-2" /> {channel === "floor_plan" ? "New Floor Plan" : "New plan"}
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <div className="border border-border bg-card p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="plan-search-input"
              placeholder="Search plans by title, style or description…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              className="rounded-none h-11 pl-9"
            />
          </div>
          <Select value={status} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="plan-status-filter" className="rounded-none h-11 w-full md:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          {/* Iter50 — Sort control */}
          <Select value={`${sortBy}:${sortDir}`} onValueChange={(v) => { const [f, d] = v.split(":"); setSortBy(f); setSortDir(d); setSkip(0); }}>
            <SelectTrigger data-testid="plan-sort" className="rounded-none h-11 w-full md:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at:desc">Recently created</SelectItem>
              <SelectItem value="created_at:asc">Oldest created</SelectItem>
              <SelectItem value="last_modified_at:desc">Recently modified</SelectItem>
              <SelectItem value="project_id:asc">Project ID (A-Z)</SelectItem>
              <SelectItem value="title:asc">Title (A-Z)</SelectItem>
              <SelectItem value="style:asc">Style (A-Z)</SelectItem>
              <SelectItem value="price_usd:asc">Price (low to high)</SelectItem>
              <SelectItem value="price_usd:desc">Price (high to low)</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu open={bulkOpen} onOpenChange={setBulkOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-none h-11 w-full md:w-40"
                disabled={selected.length === 0}
                data-testid="bulk-actions-trigger"
              >
                Bulk actions ({selected.length})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem data-testid="bulk-publish" onClick={() => runBulk("publish")}>Publish selected</DropdownMenuItem>
              <DropdownMenuItem data-testid="bulk-draft" onClick={() => runBulk("draft")}>Move to draft</DropdownMenuItem>
              <DropdownMenuItem data-testid="bulk-archive" onClick={() => runBulk("archive")}>Archive selected</DropdownMenuItem>
              <DropdownMenuItem data-testid="bulk-delete" onClick={() => runBulk("delete")} className="text-destructive focus:text-destructive">Delete selected</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Iter50 (Item #9) — Advanced filters */}
      <details className="mb-4 border border-border bg-card" data-testid="advanced-filters">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium hover:bg-muted/40">Advanced filters</summary>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border-t border-border">
          <Select value={style} onValueChange={(v) => { setStyle(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-style" className="rounded-none h-10"><SelectValue placeholder="Style" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All styles</SelectItem>{stylesMaster.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={(v) => { setCategory(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-category" className="rounded-none h-10"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{filterOpts.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={collection} onValueChange={(v) => { setCollection(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-collection" className="rounded-none h-10"><SelectValue placeholder="Collection" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All collections</SelectItem>{filterOpts.collections.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={featured} onValueChange={(v) => { setFeatured(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-featured" className="rounded-none h-10"><SelectValue placeholder="Featured" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="true">Featured only</SelectItem><SelectItem value="false">Not featured</SelectItem></SelectContent>
          </Select>
          <Select value={String(beds)} onValueChange={(v) => { setBeds(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-bedrooms" className="rounded-none h-10"><SelectValue placeholder="Bedrooms" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any beds</SelectItem>{filterOpts.bedrooms.map((b) => <SelectItem key={b} value={String(b)}>{b} bed</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(baths)} onValueChange={(v) => { setBaths(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-bathrooms" className="rounded-none h-10"><SelectValue placeholder="Bathrooms" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any baths</SelectItem>{filterOpts.bathrooms.map((b) => <SelectItem key={b} value={String(b)}>{b} bath</SelectItem>)}</SelectContent>
          </Select>
          <Select value={createdBy} onValueChange={(v) => { setCreatedBy(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-created-by" className="rounded-none h-10"><SelectValue placeholder="Created by" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any creator</SelectItem>{filterOpts.created_by.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={modifiedBy} onValueChange={(v) => { setModifiedBy(v); setSkip(0); }}>
            <SelectTrigger data-testid="filter-modified-by" className="rounded-none h-10"><SelectValue placeholder="Modified by" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any modifier</SelectItem>{filterOpts.modified_by.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2 col-span-1 md:col-span-2">
            <Input type="number" step="1" placeholder="Min price (USD)" value={priceMin} onChange={(e) => { setPriceMin(e.target.value); setSkip(0); }} className="rounded-none h-10" data-testid="filter-price-min" />
            <Input type="number" step="1" placeholder="Max price (USD)" value={priceMax} onChange={(e) => { setPriceMax(e.target.value); setSkip(0); }} className="rounded-none h-10" data-testid="filter-price-max" />
          </div>
          <Button variant="outline" onClick={() => { setStyle("all"); setCategory("all"); setCollection("all"); setBeds("all"); setBaths("all"); setFeatured("all"); setCreatedBy("all"); setModifiedBy("all"); setPriceMin(""); setPriceMax(""); setSkip(0); }} className="rounded-none h-10" data-testid="filter-clear">Clear filters</Button>
        </div>
      </details>

      {/* Table */}
      <div className="border border-border bg-card">
        <div className="hidden md:grid grid-cols-[36px_1fr_120px_100px_140px_140px_60px] items-center gap-3 px-4 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
          <div><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-plans" /></div>
          <div>Title</div>
          <div>Style</div>
          <div>Status</div>
          <div>Modified</div>
          <div>Price (USD)</div>
          <div></div>
        </div>

        {loading && <div className="p-8 text-center text-muted-foreground text-sm" data-testid="plans-loading">Loading plans…</div>}

        {!loading && items.length === 0 && (
          <div className="p-12 text-center" data-testid="plans-empty-state">
            <FileText className="mx-auto mb-3 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">{emptyMsg}</div>
            <Link to="/admin/plans/new" className="inline-block mt-4 border border-accent text-accent px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">Create a plan</Link>
          </div>
        )}

        {!loading && items.map((p) => (
          <div
            key={p.id}
            data-testid={`plan-row-${p.id}`}
            className="grid grid-cols-1 md:grid-cols-[36px_1fr_120px_100px_140px_140px_60px] gap-3 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-background/60"
          >
            <div className="md:col-span-1">
              <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`select-plan-${p.id}`} />
            </div>
            <div className="min-w-0">
              <Link to={`/admin/plans/${p.id}/edit`} className="text-sm font-medium hover:text-accent block truncate" data-testid={`plan-title-link-${p.id}`}>
                {p.title}
              </Link>
              <div className="text-[11px] text-muted-foreground truncate">
                {p.project_id && (
                  <span className="font-mono uppercase tracking-wider mr-2" data-testid={`plan-project-id-${p.id}`}>{p.project_id}</span>
                )}
                {p.bedrooms} bd · {p.bathrooms} ba · {p.sqft} sqft {p.featured ? "· ★ featured" : ""}
              </div>
            </div>
            <div className="text-sm">{p.style}</div>
            <div><StatusBadge status={p.status || "published"} /></div>
            <div className="text-xs text-muted-foreground">
              {p.last_modified_at ? new Date(p.last_modified_at).toLocaleDateString() : "—"}
              {p.last_modified_by_name && <div className="truncate">{p.last_modified_by_name}</div>}
            </div>
            {(() => {
              // Iter60A-QA(final) — Channel-aware price cell.
              // Floor Plans list: show lowest enabled fp_* tier as "From $X".
              // Premium list: show lowest enabled pdf/cad/revit tier.
              // Legacy plan.price_usd is NOT displayed on Floor Plans list —
              // it can confusingly show the Premium construction-set price
              // for a `both` plan (see QA screenshot 3).
              const channelKey = channel === "floor_plan" ? "floor_plan" : "premium";
              const server = p.pricing_by_channel?.[channelKey];
              const fromUsd = server?.from_usd;
              if (fromUsd != null && fromUsd > 0) {
                return (
                  <div className="text-sm" data-testid={`plan-price-cell-${p.id}`}>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">From</span>
                    ${Number(fromUsd).toFixed(0)}
                  </div>
                );
              }
              // No enabled tier for THIS channel → flag it.
              return (
                <div className="text-xs text-amber-700" data-testid={`plan-price-cell-${p.id}`}>
                  {channelKey === "floor_plan" ? "No fp_* price" : "No package price"}
                </div>
              );
            })()}
            <div className="justify-self-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`plan-actions-${p.id}`} className="p-2 hover:bg-background border border-transparent hover:border-border">
                    <MoreHorizontal size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem data-testid={`plan-edit-${p.id}`} onClick={() => navigate(`/admin/plans/${p.id}/edit`)}>
                    <PencilLine size={14} className="mr-2" /> Edit
                  </DropdownMenuItem>
                  {p.status !== "published" && (
                    <DropdownMenuItem data-testid={`plan-publish-${p.id}`} onClick={() => changeStatus(p.id, "published")}>
                      <Eye size={14} className="mr-2" /> Publish
                    </DropdownMenuItem>
                  )}
                  {p.status !== "draft" && (
                    <DropdownMenuItem data-testid={`plan-draft-${p.id}`} onClick={() => changeStatus(p.id, "draft")}>
                      <FileText size={14} className="mr-2" /> Move to draft
                    </DropdownMenuItem>
                  )}
                  {p.status !== "archived" && (
                    <DropdownMenuItem data-testid={`plan-archive-${p.id}`} onClick={() => changeStatus(p.id, "archived")}>
                      <Archive size={14} className="mr-2" /> Archive
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid={`plan-duplicate-${p.id}`} onClick={() => duplicate(p)}>
                    <Copy size={14} className="mr-2" /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`plan-delete-${p.id}`} onClick={() => remove(p.id)} className="text-destructive focus:text-destructive">
                    <Trash2 size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="plans-pagination">
          <div className="text-muted-foreground">Page {currentPage} of {pages} · {total} total</div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="prev-page-btn">Previous</Button>
            <Button variant="outline" className="rounded-none" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="next-page-btn">Next</Button>
          </div>
        </div>
      )}
      {/* Iter56 — Duplicate Plan Dialog */}
      <DuplicatePlanDialog
        open={Boolean(duplicateTarget)}
        onOpenChange={(o) => !o && setDuplicateTarget(null)}
        plan={duplicateTarget}
        onDuplicated={() => load()}
      />
    </AdminLayout>
  );
};

export default AdminPlansList;
